import React, { useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  ButtonBase,
  ButtonGroup,
  Stack,
  IconButton,
  Link
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  Person as PersonIcon
} from '@mui/icons-material';
import moment from 'moment';

const WeekNavigation = ({
  weekDays,
  selectedDate,
  bookingCountsByDate,
  onDateSelect,
  onPreviousWeek,
  onNextWeek,
  onGoToToday
}) => {
  const selectedDayRefs = useRef({});

  // Scroll selected day into view when it changes
  useEffect(() => {
    if (!selectedDate) return;

    const dateKey = selectedDate.format('YYYY-MM-DD');
    const selectedButton = selectedDayRefs.current[dateKey];

    if (selectedButton) {
      selectedButton.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center'
      });
    }
  }, [selectedDate, weekDays]);

  return (
    <Stack spacing={{ xs: 1, md: 2 }}>
      {/* Week navigation header - arrows + date label + today button */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{
          px: 1,
          py: 0.5,
          borderBottom: { xs: '1px solid', md: 'none' },
          borderColor: 'divider'
        }}
      >
        <IconButton size="small" onClick={onPreviousWeek}>
          <ChevronLeft />
        </IconButton>

        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="body2" fontWeight={600} sx={{ textTransform: 'capitalize' }}>
            {selectedDate
              ? `${selectedDate.format('ddd')}, ${selectedDate.format('D [de] MMM YYYY')}`
              : moment().format('ddd, D [de] MMM YYYY')}
          </Typography>
          <Link
            component="button"
            variant="body2"
            onClick={onGoToToday}
            sx={{
              fontSize: '0.75rem',
              fontWeight: 500,
              cursor: 'pointer',
              textDecoration: 'underline',
              color: 'primary.main',
              '&:hover': {
                textDecoration: 'underline'
              }
            }}
          >
            Hoje
          </Link>
        </Stack>

        <IconButton size="small" onClick={onNextWeek}>
          <ChevronRight />
        </IconButton>
      </Stack>

      {/* Day buttons - full width */}
      <Box sx={{ width: '100%' }}>
        <ButtonGroup
          variant="outlined"
          size="small"
          fullWidth
          sx={{
            '& .MuiButtonBase-root': {
              flex: 1,
              minWidth: 0
            }
          }}
        >
          {weekDays.map((day) => {
            const isSelected = day.isSame(selectedDate, 'day');
            const isToday = day.isSame(moment(), 'day');
            const dateKey = day.format('YYYY-MM-DD');
            const count = bookingCountsByDate[dateKey] || 0;

            return (
              <ButtonBase
                key={dateKey}
                ref={(el) => (selectedDayRefs.current[dateKey] = el)}
                onClick={() => onDateSelect(day)}
                sx={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                  border: '1px solid',
                  borderColor: isToday
                    ? 'primary.main'
                    : isSelected
                    ? 'primary.main'
                    : 'divider',
                  borderWidth: isToday ? 2 : 1,
                  borderRadius: '4px',
                  bgcolor: isSelected ? 'primary.main' : 'background.paper',
                  color: isSelected ? 'white' : 'text.primary',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    bgcolor: isSelected ? 'primary.dark' : 'action.hover'
                  }
                }}
              >
                {/* Content area - has padding */}
                <Box
                  sx={{
                    py: { xs: 1, sm: 1.5 },
                    px: { xs: 0.5, sm: 1 },
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center'
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      textTransform: 'capitalize',
                      fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' },
                      fontWeight: 500,
                      display: 'block'
                    }}
                  >
                    {day.format('ddd')}
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight={isToday ? 'bold' : 'normal'}
                    sx={{ fontSize: { xs: '0.85rem', sm: '0.95rem', md: '1.1rem' }, mt: 0.5 }}
                  >
                    {day.format('DD/MM')}
                  </Typography>
                </Box>

                {/* Footer - naturally fills width, no hacks needed */}
                <Box
                  sx={{
                    width: '100%',
                    py: 0.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 0.5,
                    backgroundColor: isSelected
                      ? 'rgba(255, 255, 255, 0.2)'
                      : count > 0
                      ? 'rgba(0, 0, 0, 0.08)'
                      : 'rgba(0, 0, 0, 0.04)',
                    borderTop: '1px solid',
                    borderColor: isSelected
                      ? 'rgba(255, 255, 255, 0.3)'
                      : 'rgba(0, 0, 0, 0.1)',
                    fontSize: { xs: '0.7rem', md: '0.8rem' },
                    fontWeight: 600,
                    opacity: count === 0 ? 0.5 : 1
                  }}
                >
                  {count > 0 ? (
                    <>
                      <PersonIcon sx={{ fontSize: { xs: 14, md: 16 } }} />
                      <span>{count}</span>
                    </>
                  ) : (
                    <span>—</span>
                  )}
                </Box>
              </ButtonBase>
            );
          })}
        </ButtonGroup>
      </Box>
    </Stack>
  );
};

export default WeekNavigation;
